/**
 * Domain hook for the AHP resource bridge (#1395).
 *
 * Reads file content from an environment's PowerLine-owned worktree via the
 * `ReadResource` RPC and live-tracks changes via `WatchResource` +
 * `resource.changed` domain events. The v0 live-document viewer (#1396)
 * consumes this hook to render a remote file and refresh it as the agent edits.
 *
 * Reconnect semantics: server-side watches are tied to the *environment*
 * connection, not the web event stream, so they survive a transient stream
 * blip. On reconnect we re-read every cached file to catch changes missed while
 * disconnected; we do NOT re-issue watches (that would orphan the prior
 * server-side ref). If the *environment* itself reconnected, its watch was
 * reaped server-side and live updates stop until the viewer re-watches.
 *
 * @module
 */

import { useCallback, useRef, useState, useMemo } from "react";
import type {
  GrackleEvent,
  ResourceContentState,
  UseResourcesResult,
} from "@grackle-ai/web-components";
import { warnBadPayload } from "@grackle-ai/web-components";
import type { DomainHook } from "./domainHook.js";
import { coreClient as grackleClient } from "./useGrackleClient.js";

/** Composite cache key for an environment + file URI. */
function contentKey(environmentId: string, uri: string): string {
  return `${environmentId} ${uri}`;
}

/**
 * Hook that manages resource reads/watches via ConnectRPC and routes
 * `resource.changed` domain events into a per-URI content cache.
 *
 * @returns Resource read/watch actions, a content accessor, and the domain hook.
 */
export function useResources(): UseResourcesResult {
  // Content cache lives in a ref (source of truth for synchronous reads inside
  // callbacks — no stale closures) paired with a render tick to re-render
  // consumers when content changes.
  const contentRef = useRef<Map<string, ResourceContentState>>(new Map());
  const [contentVersion, bumpRenderTick] = useState(0);
  // Active watches, so reconnect can re-read the files they cover.
  const watchesRef = useRef<Map<string, { environmentId: string; uri: string }>>(new Map());

  const storeContent = useCallback(
    (environmentId: string, uri: string, value: ResourceContentState): void => {
      contentRef.current.set(contentKey(environmentId, uri), value);
      bumpRenderTick((n) => n + 1);
    },
    [],
  );

  const readResource = useCallback(
    async (environmentId: string, uri: string): Promise<ResourceContentState> => {
      const resp = await grackleClient.readResource({ environmentId, uri, encoding: "" });
      const value: ResourceContentState = {
        data: resp.data,
        encoding: resp.encoding,
        contentType: resp.contentType,
      };
      storeContent(environmentId, uri, value);
      return value;
    },
    [storeContent],
  );

  const getResourceContent = useCallback(
    (environmentId: string, uri: string): ResourceContentState | undefined =>
      contentRef.current.get(contentKey(environmentId, uri)),
    [],
  );

  /** Drop cached content for a URI (e.g. the file was deleted). */
  const forgetContent = useCallback((environmentId: string, uri: string): void => {
    if (contentRef.current.delete(contentKey(environmentId, uri))) {
      bumpRenderTick((n) => n + 1);
    }
  }, []);

  const watchResource = useCallback(
    async (environmentId: string, uri: string, recursive: boolean = false): Promise<string> => {
      const resp = await grackleClient.watchResource({ environmentId, uri, recursive });
      watchesRef.current.set(resp.watchId, { environmentId, uri });
      return resp.watchId;
    },
    [],
  );

  const unwatchResource = useCallback(async (watchId: string): Promise<void> => {
    watchesRef.current.delete(watchId);
    await grackleClient.unwatchResource({ watchId });
  }, []);

  const handleEvent = useCallback(
    (event: GrackleEvent): boolean => {
      if (event.type !== "resource.changed") {
        return false;
      }
      const environmentId = event.payload.environmentId;
      const changes = event.payload.changes;
      if (typeof environmentId !== "string" || !Array.isArray(changes)) {
        warnBadPayload("resource.changed", "invalid payload");
        return true;
      }
      // Re-read any changed file we currently have cached so open viewers
      // refresh. Files we've never read are ignored (nothing is displaying them).
      for (const change of changes) {
        const changedUri = (change as { uri?: unknown }).uri;
        const changeType = (change as { type?: unknown }).type;
        if (typeof changedUri !== "string") {
          continue;
        }
        if (!contentRef.current.has(contentKey(environmentId, changedUri))) {
          continue;
        }
        // A deleted file: drop the cached content so stale data isn't shown.
        if (changeType === "deleted") {
          forgetContent(environmentId, changedUri);
          continue;
        }
        // Otherwise re-read; if the file became unreadable (e.g. deleted between
        // the event and the read), forget it rather than leave stale content.
        readResource(environmentId, changedUri).catch(() => {
          forgetContent(environmentId, changedUri);
        });
      }
      return true;
    },
    [readResource, forgetContent],
  );

  const onConnect = useCallback(async (): Promise<void> => {
    // Re-read every cached file to catch changes missed while disconnected.
    const keys = [...contentRef.current.keys()];
    await Promise.allSettled(
      keys.map((key) => {
        const sep = key.indexOf(" ");
        const environmentId = key.slice(0, sep);
        const uri = key.slice(sep + 1);
        return readResource(environmentId, uri);
      }),
    );
  }, [readResource]);

  const onDisconnect = useCallback((): void => {
    // Keep cached content + watch tracking; server-side watches survive a
    // transient stream blip and onConnect re-reads to resync.
  }, []);

  const domainHook: DomainHook = useMemo(
    () => ({ onConnect, onDisconnect, handleEvent }),
    [onConnect, onDisconnect, handleEvent],
  );

  return {
    readResource,
    getResourceContent,
    watchResource,
    unwatchResource,
    contentVersion,
    domainHook,
  };
}
